(() => {
  "use strict";

  const configNode = document.querySelector("[data-kulture-cart-config]");
  let config = {};

  try {
    config = configNode ? JSON.parse(configNode.textContent) : {};
  } catch (error) {
    console.error("KULTURE cart configuration could not be read.", error);
  }

  const minimumPouches = Number(config.minimumPouches) || 3;
  const routeRoot = String(config.root || (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/");
  const normalizedRoot = routeRoot.endsWith("/") ? routeRoot : `${routeRoot}/`;
  const route = (path) => `${normalizedRoot}${String(path).replace(/^\//, "")}`;
  const endpoints = {
    add: route("cart/add.js"),
    cart: route("cart.js"),
    change: route("cart/change.js"),
    update: route("cart/update.js"),
    checkout: config.checkoutUrl || route("checkout")
  };
  const pouchWeights = config.pouchWeights || { "mixed-bundle": 3 };
  const fallbackImages = config.fallbackImages || {};
  const state = {
    cart: null,
    busy: false,
    checkoutPending: false,
    lastAddTrigger: null,
    mutationQueue: Promise.resolve()
  };

  const modal = document.querySelector("[data-add-modal]");
  const modalPanel = modal ? modal.querySelector(".kulture-add-modal__panel") : null;
  const liveRegion = document.querySelector("[data-cart-live]");

  function clampQuantity(value, minimum = 0) {
    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) ? Math.max(minimum, parsed) : minimum;
  }

  function matchingConfigKey(handle, map) {
    const normalizedHandle = String(handle || "").toLowerCase();
    return Object.keys(map).find((key) => normalizedHandle === key || normalizedHandle.startsWith(`${key}-`));
  }

  function itemHandle(item) {
    if (item.handle || item.product_handle) return item.handle || item.product_handle;
    const match = String(item.url || "").match(/\/products\/([^/?#]+)/);
    return match ? match[1] : "";
  }

  function pouchWeightForItem(item) {
    const handleKey = matchingConfigKey(itemHandle(item), pouchWeights);
    if (handleKey) return Number(pouchWeights[handleKey]) || 1;

    const propertyWeight = item.properties && Number(item.properties._kulture_pouch_count);
    return Number.isFinite(propertyWeight) && propertyWeight > 0 ? propertyWeight : 1;
  }

  function calculatePhysicalPouches(cart) {
    return (cart && Array.isArray(cart.items) ? cart.items : []).reduce((total, item) => {
      return total + (clampQuantity(item.quantity, 0) * pouchWeightForItem(item));
    }, 0);
  }

  function missingPouches(cart) {
    return Math.max(0, minimumPouches - calculatePhysicalPouches(cart));
  }

  function announce(message) {
    if (!liveRegion) return;
    liveRegion.textContent = "";
    window.requestAnimationFrame(() => {
      liveRegion.textContent = message;
    });
  }

  async function requestJSON(url, options = {}) {
    const headers = {
      Accept: "application/json",
      "X-Requested-With": "XMLHttpRequest",
      ...(options.headers || {})
    };
    const response = await fetch(url, { ...options, credentials: "same-origin", headers });

    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload.description || payload.message || "The cart could not be updated. Please try again.");
    }
    return payload;
  }

  function fetchCart() {
    return requestJSON(endpoints.cart);
  }

  function formatMoney(cents, currency) {
    const amount = (Number(cents) || 0) / 100;
    try {
      return new Intl.NumberFormat("en-GB", {
        style: "currency",
        currency: currency || "GBP"
      }).format(amount);
    } catch (_) {
      return `£${amount.toFixed(2)}`;
    }
  }

  function createElement(tag, className, text) {
    const element = document.createElement(tag);
    if (className) element.className = className;
    if (text !== undefined) element.textContent = text;
    return element;
  }

  function fallbackImageForItem(item) {
    const key = matchingConfigKey(itemHandle(item), fallbackImages);
    return (key && fallbackImages[key]) || "";
  }

  function createQuantityControl(item) {
    const selector = createElement("div", "quantity-selector");
    selector.setAttribute("data-cart-quantity-selector", "");

    const decrease = createElement("button", "", "−");
    decrease.type = "button";
    decrease.dataset.cartQuantityAdjust = "-1";
    decrease.dataset.lineKey = item.key;
    decrease.setAttribute("aria-label", `Decrease ${item.product_title || item.title} quantity`);

    const input = createElement("input");
    input.type = "number";
    input.min = "0";
    input.value = String(item.quantity);
    input.inputMode = "numeric";
    input.dataset.cartQuantity = "";
    input.dataset.lineKey = item.key;
    input.setAttribute("aria-label", `${item.product_title || item.title} quantity`);

    const increase = createElement("button", "", "+");
    increase.type = "button";
    increase.dataset.cartQuantityAdjust = "1";
    increase.dataset.lineKey = item.key;
    increase.setAttribute("aria-label", `Increase ${item.product_title || item.title} quantity`);

    selector.append(decrease, input, increase);
    return selector;
  }

  function createCartLine(item, currency, compact = false) {
    const line = createElement("article", compact ? "cart-item cart-item--compact" : "cart-item");
    line.dataset.cartLine = "";
    line.dataset.lineKey = item.key;
    line.dataset.productHandle = itemHandle(item);
    line.dataset.pouchWeight = String(pouchWeightForItem(item));

    const image = createElement("img");
    const imageUrl = item.image || (item.featured_image && item.featured_image.url) || fallbackImageForItem(item);
    if (imageUrl) image.src = imageUrl;
    image.alt = item.product_title || item.title || "KULTURE product";
    image.loading = "lazy";
    if (!imageUrl) image.hidden = true;

    const details = createElement("div", "cart-item__details");
    const title = createElement(compact ? "h3" : "h2", "", item.product_title || item.title);
    const linePrice = createElement("p", "cart-item__price", formatMoney(item.final_line_price ?? item.line_price, currency));
    details.append(title, linePrice);

    Object.entries(item.properties || {}).forEach(([name, value]) => {
      if (!value || name.startsWith("_")) return;
      details.append(createElement("small", "", `${name}: ${value}`));
    });

    const actions = createElement("div", "cart-item__actions");
    actions.append(createElement("span", "cart-item__quantity-label", "Quantity"));
    actions.append(createQuantityControl(item));
    const remove = createElement("button", "cart-remove", "Remove");
    remove.type = "button";
    remove.dataset.cartRemove = "";
    remove.dataset.lineKey = item.key;
    remove.setAttribute("aria-label", `Remove ${item.product_title || item.title} from cart`);
    actions.append(remove);

    line.append(image, details, actions);
    return line;
  }

  function renderItems(container, cart, compact = false) {
    if (!container) return;
    const fragment = document.createDocumentFragment();
    cart.items.forEach((item) => fragment.append(createCartLine(item, cart.currency, compact)));
    if (!cart.items.length) fragment.append(createElement("p", "cart-empty-message", "Your cart is empty."));
    container.replaceChildren(fragment);
  }

  function modalProgressMessage(pouches) {
    if (pouches >= minimumPouches) {
      return "Your minimum order has been reached. You can now continue to checkout.";
    }
    const remaining = minimumPouches - pouches;
    const noun = remaining === 1 ? "pouch" : "pouches";
    return `You currently have ${pouches} of the minimum ${minimumPouches} pouches in your cart. Add ${remaining} more ${noun} to continue to checkout.`;
  }

  function conciseProgressMessage(pouches) {
    if (pouches >= minimumPouches) {
      return "Your minimum order has been reached. You can now continue to checkout.";
    }
    const remaining = minimumPouches - pouches;
    return `Minimum order: ${minimumPouches} pouches. Add ${remaining} more to checkout.`;
  }

  function setCheckoutState(pouches) {
    const isValid = pouches >= minimumPouches;
    document.querySelectorAll("[data-checkout-button]").forEach((button) => {
      const shouldDisable = !isValid || state.busy || state.checkoutPending;
      button.classList.toggle("is-disabled", shouldDisable);
      button.setAttribute("aria-disabled", String(shouldDisable));

      if (button instanceof HTMLButtonElement) {
        button.disabled = shouldDisable;
      } else if (shouldDisable) {
        button.removeAttribute("href");
        button.setAttribute("tabindex", "-1");
      } else {
        button.href = button.dataset.checkoutUrl || endpoints.checkout;
        button.removeAttribute("tabindex");
      }
    });

    document.querySelectorAll("[data-checkout-help]").forEach((help) => {
      help.hidden = isValid;
    });
  }

  function setCartControlsBusy(isBusy) {
    state.busy = isBusy;
    document.querySelectorAll("[data-cart-root], [data-add-modal]").forEach((container) => {
      container.setAttribute("aria-busy", String(isBusy));
    });
    document.querySelectorAll("[data-cart-quantity-adjust], [data-cart-remove], [data-cart-quantity]").forEach((control) => {
      control.disabled = isBusy;
    });
    setCheckoutState(calculatePhysicalPouches(state.cart));
  }

  function showCartError(message) {
    document.querySelectorAll("[data-cart-error]").forEach((error) => {
      error.textContent = message;
      error.hidden = false;
    });
    announce(message);
  }

  function clearCartErrors() {
    document.querySelectorAll("[data-cart-error]").forEach((error) => {
      error.textContent = "";
      error.hidden = true;
    });
  }

  function renderCartPage(cart) {
    const root = document.querySelector("[data-cart-root]");
    if (!root) return;
    const empty = root.querySelector("[data-cart-empty]");
    const form = root.querySelector("[data-cart-form]");
    if (empty) empty.hidden = cart.items.length > 0;
    if (form) form.hidden = cart.items.length === 0;
    renderItems(root.querySelector("[data-cart-page-items]"), cart, false);
  }

  function renderCustomerWelcome(cart) {
    document.querySelectorAll("[data-customer-welcome]").forEach((root) => {
      const empty = root.querySelector("[data-customer-cart-empty]");
      const hasItems = root.querySelector("[data-customer-cart-has-items]");
      if (empty) empty.hidden = cart.items.length > 0;
      if (hasItems) hasItems.hidden = cart.items.length === 0;
      renderItems(root.querySelector("[data-customer-cart-items]"), cart, true);
    });
  }

  function syncCart(cart, shouldAnnounce = false) {
    state.cart = cart;
    const pouches = calculatePhysicalPouches(cart);
    const remaining = Math.max(0, minimumPouches - pouches);

    document.querySelectorAll("[data-cart-count]").forEach((node) => {
      node.textContent = String(cart.item_count || 0);
    });
    document.querySelectorAll("[data-pouch-count]").forEach((node) => {
      node.textContent = String(pouches);
    });
    document.querySelectorAll("[data-cart-subtotal]").forEach((node) => {
      node.textContent = formatMoney(cart.total_price, cart.currency);
    });
    document.querySelectorAll("[data-cart-progress]").forEach((node) => {
      node.textContent = node.closest("[data-add-modal]") ? modalProgressMessage(pouches) : conciseProgressMessage(pouches);
    });
    document.querySelectorAll("[data-mobile-cart-status]").forEach((node) => {
      node.textContent = remaining ? `Add ${remaining} more to checkout` : "Ready to checkout";
    });

    renderItems(document.querySelector("[data-modal-cart-items]"), cart, true);
    renderCartPage(cart);
    renderCustomerWelcome(cart);
    setCheckoutState(pouches);

    document.querySelectorAll("[data-mobile-cart-toggle]").forEach((button) => {
      button.hidden = false;
    });

    if (shouldAnnounce) {
      announce(`Cart updated. ${pouches} physical ${pouches === 1 ? "pouch" : "pouches"}. ${remaining ? `Add ${remaining} more to checkout.` : "Ready to checkout."}`);
    }
  }

  function enqueueMutation(task) {
    state.mutationQueue = state.mutationQueue.catch(() => {}).then(async () => {
      clearCartErrors();
      setCartControlsBusy(true);
      try {
        const cart = await task();
        syncCart(cart, true);
        return cart;
      } catch (error) {
        showCartError(error.message || "The cart could not be updated. Please try again.");
        throw error;
      } finally {
        setCartControlsBusy(false);
      }
    });
    return state.mutationQueue;
  }

  function changeLine(lineKey, quantity) {
    return enqueueMutation(() => requestJSON(endpoints.change, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id: lineKey, quantity: clampQuantity(quantity, 0) })
    }));
  }

  function updateAllLines(updates) {
    return enqueueMutation(() => requestJSON(endpoints.update, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ updates })
    }));
  }

  function focusableModalElements() {
    if (!modalPanel) return [];
    return Array.from(modalPanel.querySelectorAll("a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex='-1'])"));
  }

  function openModal(trigger) {
    if (!modal || !modalPanel) return;
    state.lastAddTrigger = trigger || document.activeElement;
    modal.hidden = false;
    modal.setAttribute("aria-hidden", "false");
    document.body.classList.add("cart-modal-open");
    modalPanel.focus();
  }

  function closeModal() {
    if (!modal || modal.hidden) return;
    modal.hidden = true;
    modal.setAttribute("aria-hidden", "true");
    document.body.classList.remove("cart-modal-open");
    if (state.lastAddTrigger && typeof state.lastAddTrigger.focus === "function") {
      state.lastAddTrigger.focus();
    }
  }

  async function handleCheckout(event, button) {
    event.preventDefault();
    if (state.checkoutPending) return;
    state.checkoutPending = true;
    setCheckoutState(calculatePhysicalPouches(state.cart));

    try {
      const cart = await fetchCart();
      syncCart(cart);
      if (calculatePhysicalPouches(cart) < minimumPouches) {
        openModal(button);
        showCartError(`Minimum order: ${minimumPouches} pouches. Add ${missingPouches(cart)} more to checkout.`);
        return;
      }
      window.location.assign(button.dataset.checkoutUrl || endpoints.checkout);
    } catch (error) {
      showCartError(error.message || "We could not verify your cart. Please try again.");
    } finally {
      state.checkoutPending = false;
      setCheckoutState(calculatePhysicalPouches(state.cart));
    }
  }

  document.addEventListener("submit", (event) => {
    const form = event.target;

    if (form.matches("form.quick-add-form, form.product-page-form")) {
      if (!window.fetch || !form.action.includes("/cart/add")) return;
      event.preventDefault();
      if (form.dataset.busy === "true") return;

      const submitButton = form.querySelector("[data-add-button]");
      const originalLabel = submitButton ? submitButton.textContent : "";
      const formData = new FormData(form);
      formData.set("quantity", String(clampQuantity(formData.get("quantity"), 1)));
      form.dataset.busy = "true";
      form.setAttribute("aria-busy", "true");
      if (submitButton) {
        submitButton.disabled = true;
        submitButton.textContent = "Adding…";
      }

      enqueueMutation(async () => {
        await requestJSON(form.action || endpoints.add, { method: "POST", body: formData });
        return fetchCart();
      }).then(() => {
        openModal(submitButton || form);
        if (submitButton && form.matches(".preorder-card__quick-add")) {
          submitButton.textContent = "Added to bag";
          window.setTimeout(() => {
            if (form.dataset.busy !== "true") submitButton.textContent = originalLabel;
          }, 1600);
        }
      }).catch(() => {}).finally(() => {
        form.dataset.busy = "false";
        form.setAttribute("aria-busy", "false");
        if (submitButton) {
          submitButton.disabled = false;
          if (!form.matches(".preorder-card__quick-add")) submitButton.textContent = originalLabel;
        }
      });
      return;
    }

    if (form.matches("[data-cart-form]")) {
      event.preventDefault();
      const updates = {};
      form.querySelectorAll("[data-cart-quantity][data-line-key]").forEach((input) => {
        updates[input.dataset.lineKey] = clampQuantity(input.value, 0);
      });
      updateAllLines(updates).catch(() => {});
    }
  });

  document.addEventListener("click", (event) => {
    const checkoutButton = event.target.closest("[data-checkout-button], a[href$='/checkout']");
    if (checkoutButton) {
      handleCheckout(event, checkoutButton);
      return;
    }

    const productAdjust = event.target.closest("[data-product-quantity-adjust]");
    if (productAdjust) {
      const selector = productAdjust.closest("[data-quantity-selector]");
      const input = selector && selector.querySelector("[data-product-quantity]");
      if (!input) return;
      input.value = String(clampQuantity(Number(input.value) + Number(productAdjust.dataset.productQuantityAdjust), 1));
      input.dispatchEvent(new Event("change", { bubbles: true }));
      return;
    }

    const cartAdjust = event.target.closest("[data-cart-quantity-adjust]");
    if (cartAdjust) {
      if (state.busy) return;
      const line = cartAdjust.closest("[data-cart-line]");
      const input = line && line.querySelector("[data-cart-quantity]");
      if (!input) return;
      const nextQuantity = clampQuantity(Number(input.value) + Number(cartAdjust.dataset.cartQuantityAdjust), 0);
      input.value = String(nextQuantity);
      changeLine(cartAdjust.dataset.lineKey, nextQuantity).catch(() => {});
      return;
    }

    const removeButton = event.target.closest("[data-cart-remove]");
    if (removeButton) {
      if (state.busy) return;
      changeLine(removeButton.dataset.lineKey, 0).catch(() => {});
      return;
    }

    if (event.target.closest("[data-continue-shopping]")) {
      closeModal();
      return;
    }

    const mobileCart = event.target.closest("[data-mobile-cart-toggle]");
    if (mobileCart) {
      if (state.cart) {
        openModal(mobileCart);
      } else {
        fetchCart().then((cart) => {
          syncCart(cart);
          openModal(mobileCart);
        }).catch((error) => showCartError(error.message));
      }
      return;
    }

    if (event.target === modal) closeModal();
  });

  document.addEventListener("change", (event) => {
    if (event.target.matches("[data-product-quantity]")) {
      event.target.value = String(clampQuantity(event.target.value, 1));
      return;
    }
    if (event.target.matches("[data-cart-quantity]")) {
      if (state.busy) return;
      const quantity = clampQuantity(event.target.value, 0);
      event.target.value = String(quantity);
      changeLine(event.target.dataset.lineKey, quantity).catch(() => {});
    }
  });

  document.addEventListener("keydown", (event) => {
    if (!modal || modal.hidden) return;
    if (event.key === "Escape") {
      event.preventDefault();
      closeModal();
      return;
    }
    if (event.key !== "Tab") return;

    const focusable = focusableModalElements();
    if (!focusable.length) {
      event.preventDefault();
      modalPanel.focus();
      return;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  document.querySelectorAll("[data-mobile-cart-toggle]").forEach((button) => {
    button.hidden = false;
  });

  fetchCart().then((cart) => syncCart(cart)).catch((error) => {
    showCartError(error.message || "The cart could not be loaded.");
  });

  window.KultureCart = Object.freeze({
    calculatePhysicalPouches,
    pouchWeightForItem,
    refresh: () => fetchCart().then((cart) => {
      syncCart(cart);
      return cart;
    })
  });
})();
